import { Component, DestroyRef, OnInit, signal, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Crop, getCrops } from '@next/arable-lib/crop';
import { DIALOG_RETURN_TYPES, DialogService } from '@next/core-lib/dialog';
import { LocaleService, TranslatePipe } from '@next/core-lib/i18n';
import { SiteState } from '@next/core-lib/site';
import { Store } from '@ngrx/store';

import { combineLatest, filter, map, switchMap, take, tap } from 'rxjs';
import { selectCropYear } from 'src/app/store/selectors';
import { CropYearService } from 'src/app/shared/services/crop-year.service';
import {
  GenericSoilQuestions,
  GenericSoilQuestionsRequest,
  ProcessedAnswers,
  ProcessedAnswersWithForm,
} from 'src/app/tga/shared/models/models';
import { fetchGenericSoilQuestions, postGenericSoilQuestions } from 'src/app/tga/store/tga.actions';
import { selectGenericSoilQuestions } from 'src/app/tga/store/tga.selectors';

import { GenericSoilQuestionsService } from './generic-soil-questions.service';
import {
  DepthStorendeLaag,
  GrasslandOrGrassSeed,
  MainSoilTillageMethod,
  MaterialState,
  Structuurbederfsource,
  TgaInputType,
  TGAQuestion,
} from '../../../models/question-models';
import { updateVisibilityOnDependantQuestions } from '../../question-helpers';
import { BaseGenericQuestionnaireComponent } from '../base-generic-questionnaire/base-generic-questionnaire.component';

@Component({
  selector: 'app-generic-soil-questionnaire',
  templateUrl: './generic-soil-questionnaire.component.html',
  styleUrl: './generic-soil-questionnaire.component.scss',
  imports: [BaseGenericQuestionnaireComponent, TranslatePipe],
})
export class GenericSoilQuestionnaireComponent implements OnInit {
  protected readonly isCropYearReadOnly = inject(CropYearService).isReadOnly;
  private readonly store = inject<Store<SiteState>>(Store);
  private readonly questionService = inject(GenericSoilQuestionsService);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialogService = inject(DialogService);
  private readonly locale = inject(LocaleService);

  readonly cropYear = signal<number | null>(null);
  readonly soilQuestions = signal<TGAQuestion<TgaInputType>[]>([]);
  readonly crops = signal<Crop[]>([]);
  readonly soilQuestions$ = this.store.select(selectGenericSoilQuestions);
  private readonly selectedCropYear$ = this.store.select(selectCropYear);
  formReady = signal(false);
  formGroup: FormGroup = new FormGroup({});
  private hasShownConfirmation = false;
  private previousFormValues: Record<string, unknown> = {};
  readonly greenManureCrops$ = this.store.select(getCrops).pipe(
    map((crop) => {
      // Create a shallow copy to avoid mutating the store data, then sort alphabetically
      const sortedCrops = [...crop].sort((a, b) => {
        if (!a.name || !b.name) {
          return 0;
        }
        return a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1;
      });

      // Filter green manure crops
      const greenManureCropfields = sortedCrops
        .filter((c) => c.isGreenManure)
        .map((c) => ({
          key: c.id.toString(),
          value: c.name,
        }));

      // This crop is used to represent 'unknown' green manure crops in the questionnaire
      const unknownGreenManureCrop = sortedCrops.find((c) => c.agrifirmCrop?.id === 1015);
      if (unknownGreenManureCrop) {
        greenManureCropfields.push({
          key: unknownGreenManureCrop.id.toString(),
          value: unknownGreenManureCrop.name,
        });
      }

      return greenManureCropfields;
    }),
  );
  private readonly params$ = this.route.params.pipe(
    takeUntilDestroyed(this.destroyRef),
    map(this.questionService.extractRouteParams.bind(this.questionService)),
    filter(this.questionService.validateRouteParams.bind(this.questionService)),
    tap(({ cropYear }) => {
      this.cropYear.set(cropYear);
    }),
  ) as ReturnType<typeof this.route.params.pipe<{ cropYear: number }>>;

  private readonly questions$ = combineLatest([
    this.greenManureCrops$.pipe(filter((cropField) => cropField !== null && cropField.length > 0)),
    this.soilQuestions$,
  ]).pipe(
    tap(([greenManureCrops]) => {
      this.questionService.initializeSoilQuestions(greenManureCrops);
    }),
    map(() => {
      return this.questionService.initValues();
    }),
  );

  ngOnInit(): void {
    this.store
      .select(getCrops)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((crops) => this.crops.set(crops || []));

    this.params$
      .pipe(
        switchMap(() =>
          this.questions$.pipe(
            take(1),
            tap((questions) => this.checkAndFetchMissingQuestions(questions)),
          ),
        ),
      )
      .subscribe();

    this.questions$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((questions) => questions.length > 0),
      )
      .subscribe((questions) => {
        this.soilQuestions.set(questions);
        this.buildForm(questions);
      });
  }
  private checkAndFetchMissingQuestions(_questions: TGAQuestion<TgaInputType>[]) {
    this.selectedCropYear$.pipe(take(1)).subscribe((cropYear) => {
      this.store.dispatch(fetchGenericSoilQuestions({ cropYear }));
    });
  }

  private buildForm(questions: TGAQuestion<TgaInputType>[]): void {
    if (questions.length === 0) {
      return;
    }

    const group: { [key: string]: AbstractControl } = {};

    for (const question of questions) {
      let initialValue: TgaInputType | Date | undefined = question.value;

      // Convert year number to Date object for date-year questions
      if (question.controlType === 'date-year' && typeof question.value === 'number') {
        initialValue = new Date(question.value, 0, 1);
      }

      const control = new FormControl(initialValue);
      this.addQuestionValidators(question, control);
      group[question.key] = control;
    }
    this.formGroup = new FormGroup(group);
    updateVisibilityOnDependantQuestions(this.soilQuestions(), this.formGroup);

    // Store initial values as previous values
    this.previousFormValues = JSON.parse(JSON.stringify(this.formGroup.value));
    this.formReady.set(true);
  }

  private addQuestionValidators(question: TGAQuestion<TgaInputType>, control: AbstractControl): void {
    if (question.required) {
      control.addValidators(Validators.required);
    }
  }

  answersUpdated(data: ProcessedAnswersWithForm): void {
    if (this.isCropYearReadOnly()) return;

    this.soilQuestions$.pipe(take(1)).subscribe((questionsWithStatus) => {
      const shouldShowDialog = questionsWithStatus?.hasFilledInSpecific === true && !this.hasShownConfirmation;

      if (shouldShowDialog) {
        this.dialogService
          .confirm({
            title: this.locale.translate('TGA.Questionnaire.Generic.OverwriteConfirmMessage'),
            text: this.locale.translate('TGA.Questionnaire.Generic.OverwriteConfirmDescription'),
            pushHistoryState: false,
          })
          .subscribe((result) => {
            if (this.isCropYearReadOnly()) return;

            if (result.status === DIALOG_RETURN_TYPES.confirm) {
              this.hasShownConfirmation = true;
              if (this.formGroup.valid) {
                this.validateAndSubmit(data);
              }
            } else {
              // User cancelled - revert form to previous values
              this.revertFormValues();
            }
          });
      } else {
        this.validateAndSubmit(data);
      }
    });
  }

  private validateAndSubmit(data: ProcessedAnswersWithForm) {
    if (this.isCropYearReadOnly()) return;

    const { answers: processedAnswers, formGroup } = data;
    if (!formGroup.valid) {
      return;
    }

    const cropYear = this.cropYear();

    if (!cropYear) {
      return;
    }

    this.submitAnswers(processedAnswers);
  }

  private submitAnswers(processedAnswers: ProcessedAnswers): void {
    if (this.isCropYearReadOnly()) return;

    const soilQuestions: GenericSoilQuestions = {
      lastHarvestedYear: this.questionService.processDateYearAnswer(
        processedAnswers[GenericSoilQuestionsService.lastHarvestedYear] as unknown as Date,
      ),

      mainSoilTillageMethod:
        MainSoilTillageMethod[
        processedAnswers[GenericSoilQuestionsService.mainSoilTillageMethod] as keyof typeof MainSoilTillageMethod
        ],
      isInLuwte: this.questionService.processYesNoAnswer(processedAnswers[GenericSoilQuestionsService.isInLuwte]),
      hadGrasslandOrGrassSeed:
        GrasslandOrGrassSeed[
        processedAnswers[GenericSoilQuestionsService.hadGrasslandOrGrassSeed] as keyof typeof GrasslandOrGrassSeed
        ],
      greenManureCropId: this.questionService.processGreenManureCropId(
        processedAnswers[GenericSoilQuestionsService.greenManureCropId],
      ),

      ...this.questionService.processDiseasesOnThePlot(processedAnswers),

      hasSoilVariety: this.questionService.processYesNoUnknownAnswer(
        processedAnswers[GenericSoilQuestionsService.hasSoilVariety],
      ),
      soilVarietyDifferences: processedAnswers[GenericSoilQuestionsService.soilVarietyDifferences] as string,
      isStuifgevoelig: this.questionService.processYesNoAnswer(
        processedAnswers[GenericSoilQuestionsService.IsStuifgevoelig],
      ),
      hasSoilScan: this.questionService.processYesNoAnswer(processedAnswers[GenericSoilQuestionsService.hasSoilscan]),

      structuurbederfSource:
        Structuurbederfsource[
        processedAnswers[GenericSoilQuestionsService.structuurbederfsource] as keyof typeof Structuurbederfsource
        ],

      depthStorendeLaag:
        DepthStorendeLaag[
        processedAnswers[GenericSoilQuestionsService.depthStorendeLaag] as keyof typeof DepthStorendeLaag
        ],

      isBeredenUnderWetConditions: this.questionService.processYesNoAnswer(
        processedAnswers[GenericSoilQuestionsService.isBeredenUnderWetConditions],
      ),
      hasWorkingDrainage: this.questionService.processYesNoAnswer(
        processedAnswers[GenericSoilQuestionsService.hasWorkingDrainage],
      ),
      hasLaagten: this.questionService.processYesNoAnswer(processedAnswers[GenericSoilQuestionsService.hasLaagten]),

      knolbehandelingMaterial:
        MaterialState[
        processedAnswers[GenericSoilQuestionsService.knolbehandelingMaterial] as keyof typeof MaterialState
        ],

      ...this.questionService.processIsExpectedInUitgangsmateriaal(
        processedAnswers[GenericSoilQuestionsService.isExpectedInUitgangsmateriaal],
      ),
      hasAManalysis: this.questionService.processYesNoUnknownAnswer(
        processedAnswers[GenericSoilQuestionsService.hasAManalysis],
      ),
      hasAaltjesAnalysis: this.questionService.processYesNoAnswer(
        processedAnswers[GenericSoilQuestionsService.hasAaltjesAnalysis],
      ),

      // Unused fields, send them for now
      hasIrrigation: false,
      hasProefkuil: false,
      cropRotation: [],
      totalYearsWithGrassGrainCorn: 0,
      totalYearsWithGrassGrainCorn_IsManual: false,
    };

    const answersPayload: GenericSoilQuestionsRequest = {
      ...soilQuestions,
      cropYear: new Date().getFullYear(),
    };

    this.store.dispatch(postGenericSoilQuestions({ answers: answersPayload }));
  }

  private revertFormValues(): void {
    // Restore the previous form values by rebuilding the form
    // This ensures checkboxes and all form controls are properly reset
    const currentQuestions = this.soilQuestions();

    // Update question values with previous form values
    const updatedQuestions = currentQuestions.map((question) => ({
      ...question,
      value: this.previousFormValues[question.key] as TgaInputType | undefined,
    }));
    // Rebuild the form with previous values
    this.soilQuestions.set(updatedQuestions as TGAQuestion<TgaInputType>[]);
    this.buildForm(updatedQuestions as TGAQuestion<TgaInputType>[]);

    // Reset the confirmation flag to allow another submission after revert
    this.hasShownConfirmation = false;
  }
}
